import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getTimetables, deleteAllTimetables } from '../../_services/schedules.service';
import { Timetable } from '../../_models/schedule.model';
import TeacherSchedule from './TeacherSchedule';
import BirdsEyeView from './BirdsEyeView';
import AnalyticsTab from './AnalyticsTab';
import InstructorDashboard from '../instructor-dashboard/InstructorDashboard';
import Sidebar from '../../components/Sidebar/Sidebar';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  TextField,
  CircularProgress,
  Paper,
} from '@material-ui/core';
import { Delete as DeleteIcon, Warning as WarningIcon } from '@material-ui/icons';
import './styles.css';

const TAB_NAMES: Record<string, number> = {
  'timetables': 0,
  'my-schedule': 1,
  'birds-view': 2,
  'analytics': 3,
  'my-teaching-plan': 4,
};

const ScheduleList: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [timetables, setTimetables] = useState<Timetable[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = new URLSearchParams(location.search).get('tab');
    return tabParam && TAB_NAMES[tabParam] !== undefined ? TAB_NAMES[tabParam] : 0;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Delete All state
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [includeUnassignedSlots, setIncludeUnassignedSlots] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteResultOpen, setDeleteResultOpen] = useState(false);
  const [deleteResult, setDeleteResult] = useState<any>(null);

  useEffect(() => {
    loadTimetables();
  }, []);

  const loadTimetables = async () => {
    setLoading(true);
    try {
      const response = await getTimetables();

      if (response.success && response.data) {
        setTimetables(response.data);
      } else {
        console.error('Failed to load timetables');
        setTimetables([]);
      }
    } catch (error) {
      console.error('Error loading timetables:', error);
      setTimetables([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (id: number) => {
    history.push(`/schedules-templates/${id}`);
  };

  const handleBack = () => {
    history.push('/home');
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const filteredTimetables = timetables.filter(timetable => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      timetable.name?.toLowerCase().includes(query) ||
      timetable.remarks?.toLowerCase().includes(query)
    );
  });

  // Delete All handlers
  const handleDeleteAllClick = () => {
    if (timetables.length === 0) {
      alert('No timetables to delete');
      return;
    }
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllDialogClose = () => {
    setDeleteAllDialogOpen(false);
    setIncludeUnassignedSlots(false);
  };

  const handleProceedToConfirm = () => {
    setDeleteAllDialogOpen(false);
    setDeleteConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setDeleteConfirmDialogOpen(false);
    setDeleteConfirmText('');
  };

  const handleDeleteAll = async () => {
    if (deleteConfirmText !== 'DELETE ALL') {
      return;
    }

    try {
      setDeleting(true);
      const response = await deleteAllTimetables(includeUnassignedSlots);

      if (response.success) {
        setDeleteResult(response.data);
        setDeleteConfirmDialogOpen(false);
        setDeleteConfirmText('');
        setIncludeUnassignedSlots(false);
        setDeleteResultOpen(true);

        // Reload timetables
        await loadTimetables();
      } else {
        alert('Failed to delete timetables: ' + (response.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error deleting timetables:', error);
      alert('Error deleting timetables: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleResultDialogClose = () => {
    setDeleteResultOpen(false);
    setDeleteResult(null);
  };

  return (
    <>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <Box className="schedule-list-container" p={3}>
        <Box display="flex" alignItems="center" style={{ marginBottom: '16px' }}>
          <Typography variant="h4">
            Schedules and Resources
          </Typography>
        </Box>

        <AppBar position="static" color="default" style={{ marginBottom: 20 }}>
          <Toolbar style={{ minHeight: '48px', paddingLeft: 0, paddingRight: '16px', display: 'flex', justifyContent: 'space-between' }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="schedules tabs" variant="scrollable" scrollButtons="auto" style={{ flex: 1 }}>
              <Tab label="Timetable Schedules" />
              <Tab label="My Schedule" />
              <Tab label="Birds View" />
              <Tab label="Analytics" />
              <Tab label="My Teaching Plan" />
            </Tabs>
            <Box display="flex" style={{ gap: '12px' }}>
              {activeTab === 0 && timetables.length > 0 && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteAllClick}
                  style={{
                    borderColor: 'var(--status-danger)',
                    color: 'var(--status-danger)',
                    textTransform: 'none',
                    padding: '6px 16px',
                    fontWeight: 500,
                    minHeight: '36px',
                    flexShrink: 0,
                  }}
                  title="Delete all timetables and their activity slots"
                >
                  Delete All
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        {activeTab === 0 && (
          <div className="search-filters">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search timetables..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  border: '1px solid var(--border-default)',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
            </div>
          </div>
        )}

        {activeTab === 0 ? (
        loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                border: '4px solid var(--border-default)',
                borderTop: '4px solid var(--brand-primary)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto',
              }}
            />
            <p style={{ marginTop: '16px', color: 'var(--text-muted)' }}>Loading timetables...</p>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        ) : filteredTimetables.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
            <h3 style={{ color: 'var(--text-strong)', marginBottom: '8px' }}>
              {searchQuery ? 'No matching timetables found' : 'No timetables available'}
            </h3>
            <p style={{ color: 'var(--text-muted)' }}>
              {searchQuery
                ? 'Try adjusting your search query'
                : 'There are no timetables in the system yet'}
            </p>
          </div>
        ) : (
          <div className="schedules-grid">
            {filteredTimetables.map((timetable) => (
              <div key={timetable.id} className="schedule-card">
                <div className="schedule-card-header">
                  <h3 className="schedule-name">{timetable.name || 'Untitled Timetable'}</h3>
                  <div className="batch-count-badge">
                    {timetable.batchCount || 0} {timetable.batchCount === 1 ? 'cohort' : 'cohorts'}
                  </div>
                </div>

                <div className="schedule-dates">
                  <div className="date-item">
                    <span className="date-label">From:</span>
                    <span className="date-value">{formatDate(timetable.effectiveFrom)}</span>
                  </div>
                  <div className="date-item">
                    <span className="date-label">To:</span>
                    <span className="date-value">{formatDate(timetable.effectiveTo)}</span>
                  </div>
                </div>

                {timetable.remarks && (
                  <div className="schedule-remarks">
                    <p>{timetable.remarks}</p>
                  </div>
                )}

                <div className="schedule-actions">
                  <button
                    className="view-button"
                    onClick={() => handleViewClick(timetable.id)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : activeTab === 1 ? (
        <TeacherSchedule />
      ) : activeTab === 2 ? (
        <BirdsEyeView />
      ) : activeTab === 3 ? (
        <AnalyticsTab />
      ) : activeTab === 4 ? (
        <InstructorDashboard />
      ) : null}

        {/* Stage 1: Initial Warning Dialog */}
        <Dialog
          open={deleteAllDialogOpen}
          onClose={handleDeleteAllDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
              <WarningIcon style={{ color: '#ff9800', fontSize: 28 }} />
              <span>Delete All Timetables?</span>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Paper
              elevation={0}
              style={{
                backgroundColor: 'var(--status-warning-bg)',
                border: '1px solid var(--status-warning)',
                padding: '16px',
                marginBottom: '16px',
              }}
            >
              <Typography variant="body1" style={{ fontWeight: 600, marginBottom: '8px' }}>
                ⚠️ This action cannot be undone!
              </Typography>
              <Typography variant="body2">
                You are about to delete {timetables.length} timetable{timetables.length !== 1 ? 's' : ''} and all their associated activity slots.
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              style={{
                backgroundColor: 'var(--surface-page)',
                padding: '16px',
                marginBottom: '16px',
              }}
            >
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '8px' }}>
                📋 What will be deleted:
              </Typography>
              <Typography variant="body2" component="div">
                ✓ All timetables ({timetables.length})<br />
                ✓ All activity slots assigned to timetables<br />
                ✓ All related mappings
              </Typography>
            </Paper>

            <FormControlLabel
              control={
                <Checkbox
                  checked={includeUnassignedSlots}
                  onChange={(e) => setIncludeUnassignedSlots(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    Also delete unassigned activity slots
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Unassigned activity slots are slots created in the Manual Scheduling Board but never published to a timetable.
                    These are typically draft or work-in-progress sessions.
                  </Typography>
                  <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: '4px' }}>
                    • <strong>Checked:</strong> Delete ALL activity slots (including drafts)<br />
                    • <strong>Unchecked:</strong> Keep unassigned slots for future use
                  </Typography>
                </Box>
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteAllDialogClose} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleProceedToConfirm}
              variant="contained"
              style={{ backgroundColor: 'var(--status-danger)', color: 'white' }}
            >
              Continue →
            </Button>
          </DialogActions>
        </Dialog>

        {/* Stage 2: Final Confirmation Dialog */}
        <Dialog
          open={deleteConfirmDialogOpen}
          onClose={handleConfirmDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle style={{ backgroundColor: 'var(--status-danger)', color: 'white' }}>
            <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
              <WarningIcon style={{ fontSize: 28 }} />
              <span>🔴 Final Confirmation Required</span>
            </Box>
          </DialogTitle>
          <DialogContent style={{ marginTop: '16px' }}>
            <Typography variant="body1" style={{ fontWeight: 600, marginBottom: '16px' }}>
              You are about to permanently delete:
            </Typography>

            <Paper
              elevation={2}
              style={{
                padding: '16px',
                marginBottom: '16px',
                backgroundColor: 'var(--surface-page)',
              }}
            >
              <Typography variant="body2" style={{ marginBottom: '8px' }}>
                🗓️ <strong>Timetables:</strong> {timetables.length}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '8px' }}>
                📝 <strong>Assigned Activity Slots:</strong> Will be deleted
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '8px' }}>
                🔗 <strong>Related Mappings:</strong> Will be deleted
              </Typography>
              {includeUnassignedSlots && (
                <Typography variant="body2" style={{ color: 'var(--status-danger)', fontWeight: 600 }}>
                  ⚠️ <strong>Unassigned Activity Slots:</strong> Will also be deleted
                </Typography>
              )}
            </Paper>

            <Paper
              elevation={0}
              style={{
                backgroundColor: 'var(--status-danger-bg)',
                border: '1px solid var(--status-danger)',
                padding: '12px',
                marginBottom: '16px',
              }}
            >
              <Typography variant="body1" style={{ color: 'var(--status-danger-text)', fontWeight: 600, textAlign: 'center' }}>
                ⚠️ THIS ACTION CANNOT BE UNDONE!
              </Typography>
            </Paper>

            <Typography variant="body2" style={{ marginBottom: '8px' }}>
              Type <strong>DELETE ALL</strong> to confirm:
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE ALL"
              disabled={deleting}
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmDialogClose} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAll}
              variant="contained"
              style={{
                backgroundColor: deleteConfirmText === 'DELETE ALL' ? '#dc3545' : '#ccc',
                color: 'white',
              }}
              disabled={deleteConfirmText !== 'DELETE ALL' || deleting}
              startIcon={deleting ? <CircularProgress size={20} style={{ color: 'white' }} /> : <DeleteIcon />}
            >
              {deleting ? 'Deleting...' : 'Delete All Timetables'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Stage 3: Result Dialog */}
        <Dialog
          open={deleteResultOpen}
          onClose={handleResultDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle style={{ backgroundColor: 'var(--status-success)', color: 'white' }}>
            ✅ Deletion Complete
          </DialogTitle>
          <DialogContent style={{ marginTop: '16px' }}>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
              Successfully deleted:
            </Typography>
            {deleteResult && (
              <Paper elevation={0} style={{ backgroundColor: 'var(--surface-page)', padding: '16px' }}>
                <Typography variant="body2" style={{ marginBottom: '8px' }}>
                  • <strong>{deleteResult.deletedCounts?.timetables || 0}</strong> timetables
                </Typography>
                <Typography variant="body2" style={{ marginBottom: '8px' }}>
                  • <strong>{deleteResult.deletedCounts?.mappedActivitySlots || 0}</strong> activity slots (assigned to timetables)
                </Typography>
                <Typography variant="body2" style={{ marginBottom: '8px' }}>
                  • <strong>{deleteResult.deletedCounts?.totalMappings || 0}</strong> mappings
                </Typography>
                {deleteResult.unassignedSlotsDeleted && (
                  <Typography variant="body2" style={{ color: 'var(--status-danger)', fontWeight: 600 }}>
                    • <strong>{deleteResult.deletedCounts?.unmappedActivitySlots || 0}</strong> unassigned activity slots
                  </Typography>
                )}
              </Paper>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleResultDialogClose} variant="contained" color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default ScheduleList;
